<script setup lang="ts">
/**
 * Laadindicator: een draaiende ring.
 *
 *   <VSpinner />                      standaard: sm, lichte ring met primary boog
 *   <VSpinner size="lg" />            sectie/pagina laden
 *   <VSpinner tone="current" />       op een gekleurde achtergrond (knop): volgt de tekstkleur
 *
 * Vervangt ±27 losse `animate-spin`-constructies in 4 vormen (randen in 5 maten en 4
 * kleuren, een flowbite-SVG, Tabler IconLoader, een oude `.loading`-klasse).
 *
 * Toegankelijk: role="status" met een label (standaard "Laden..").
 */
import { trans } from 'laravel-vue-i18n'
import { computed } from 'vue'

const props = withDefaults(
    defineProps<{
        size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
        /**
         * track: lichtgrijze ring met een boog in de whitelabel-kleur (inhoud laden).
         * current: boog in de huidige tekstkleur met een open stuk (in knoppen en op
         * gekleurde vlakken).
         */
        tone?: 'track' | 'current'
        /** Toegankelijke naam; standaard de vertaling van "Laden". */
        label?: string | null
    }>(),
    {
        size: 'sm',
        tone: 'track',
        label: null,
    }
)

const SIZE = {
    xs: 'h-3 w-3 border-2',
    sm: 'h-4 w-4 border-2',
    md: 'h-8 w-8 border-2',
    lg: 'h-12 w-12 border-4',
    xl: 'h-16 w-16 border-4',
} as const

const TONE = {
    track: 'border-gray-200 border-t-primary dark:border-gray-700 dark:border-t-primary',
    // text-inherit: de spinner is een <span>, en global.scss zet `body span` op
    // --main-text-color (#555). Zonder deze klasse kreeg de ring in een primaire knop
    // die grijze kleur i.p.v. de knoptekstkleur (review #6967 R201, GH #6972).
    current: 'border-current border-t-transparent text-inherit',
} as const

const classes = computed(() => [
    'v-spinner inline-block shrink-0 animate-spin rounded-full',
    SIZE[props.size],
    TONE[props.tone],
])

const accessibleLabel = computed(() => props.label ?? trans('global.loading'))
</script>

<template>
    <span :class="classes" role="status" :aria-label="accessibleLabel"></span>
</template>
